import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";

import { RetrievalRequestBody, RetrievalResponse } from "../types/Chatbot/Chat";

const dbUrl = process.env.REACT_APP_API_ENDPOINT;

// Reusable Axios client instance
const chatClient = axios.create({
  // baseURL: "https://facade-service.prd.paceai2050.com/api",
  baseURL: "https://q359ayyam7.execute-api.us-east-1.amazonaws.com/prd/api",
});

// Helper function for a delay (used in retries)
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// POST function to initiate retrieval process
export const postRetrievalData = async ({ prompt, chatType, question }: { prompt: string; chatType: "unit" | "insurance"; question: string }): Promise<any> => {
  const apiKey = chatType === "unit" ? process.env.REACT_APP_TRESLE_CHAT_API : process.env.REACT_APP_TRESLE_INSURANCE_API;

  const { accessToken } = (await fetchAuthSession()).tokens ?? {};

  // console.log("accessToken", accessToken?.toString());
  // console.log("idToken", idToken?.toString());
  // console.log("auth", auth);

  if (!apiKey) {
    throw new Error(`API key for chatType "${chatType}" is missing!`);
  }

  const body: RetrievalRequestBody = {
    user_details: {
      user_id: "admin",
      access_details: {
        db_policy_details: [
          {
            database_host: "*",
            database_name: "*",
          },
        ],
        iam_policy_details: [
          {
            policy_name: "AdministratorAccess",
            policy_arn: "arn:aws:iam::aws:policy/AdministratorAccess",
          },
          {
            policy_name: "AmazonEC2ContainerRegistryFullAccess",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEC2ContainerRegistryFullAccess",
          },
          {
            policy_name: "AmazonEC2FullAccess",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEC2FullAccess",
          },
          {
            policy_name: "AmazonEKSClusterPolicy",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEKSClusterPolicy",
          },
        ],
        json_nodes_policy_details: [
          {
            source_prefix: "*",
          },
        ],
      },
    },
    session_id: "test",
    additional_prompt: "",
    app_name: chatType === "unit" ? "Front_Facing_Chatbot" : "Insurance_Inquiry_Application",
    query: prompt,
  };

  try {
    const response = await chatClient.post<RetrievalResponse>("/v2.0/retrieval/full", body, {
      headers: {
        "X-Api-Key": apiKey,
        "Content-Type": "application/json",
        // Authorization: `Bearer ${accessToken}`,
        // Authorization: axios.defaults.headers.common["Authorization"],
      },
    });

    if (!accessToken) {
      throw new Error("No access token found. Please log in again.");
    }

    if (response.status === 200 && response.data.reference_id) {
      return await getFullResponse(response.data.reference_id, apiKey, accessToken.toString(), chatType, question);
    }

    throw new Error("Failed to retrieve reference ID from API response.");
  } catch (error) {
    console.error("Error in postRetrievalData:", error);
    throw error; // Propagate error for external handling
  }
};

// GET function to retrieve the chat response
export const getFullResponse = async (referenceId: string, apiKey: string, accessToken: string, chatType: "unit" | "insurance", question: string): Promise<any> => {
  const response = await chatClient.get<any>(`/v1.0/history/retrieval?reference_id=${referenceId}`, {
    headers: {
      "X-Api-Key": apiKey,
      "Content-Type": "application/json",
      // Authorization: `Bearer ${accessToken}`,
      // Authorization: axios.defaults.headers.common["Authorization"],
    },
  });

  if (response.status === 200 || response.status === 202) {
    const data = response.data;
    // Check if the response is still in progress
    if (data.reference_id) {
      await delay(1000);
      return getFullResponse(referenceId, apiKey, accessToken, chatType, question);
    } else {
      // Base case: No new reference_id, return the final data
      return postRetrievalDataChat({ chatType, data, question });
      //return data;
    }
  } else {
    throw new Error("Failed to retrieve chat response from API.");
  }
};

export const postRetrievalDataChat = async ({ chatType, data, question }: { chatType: "unit" | "insurance"; data: any; question: string }): Promise<any> => {
  const apiKey = chatType === "unit" ? process.env.REACT_APP_TRESLE_CHAT_API : process.env.REACT_APP_TRESLE_INSURANCE_API;

  const { accessToken } = (await fetchAuthSession()).tokens ?? {};

  if (!apiKey) {
    throw new Error(`API key for chatType "${chatType}" is missing!`);
  }

  const body: RetrievalRequestBody = {
    user_details: {
      user_id: "admin",
      access_details: {
        db_policy_details: [
          {
            database_host: "*",
            database_name: "*",
          },
        ],
        iam_policy_details: [
          {
            policy_name: "AdministratorAccess",
            policy_arn: "arn:aws:iam::aws:policy/AdministratorAccess",
          },
          {
            policy_name: "AmazonEC2ContainerRegistryFullAccess",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEC2ContainerRegistryFullAccess",
          },
          {
            policy_name: "AmazonEC2FullAccess",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEC2FullAccess",
          },
          {
            policy_name: "AmazonEKSClusterPolicy",
            policy_arn: "arn:aws:iam::aws:policy/AmazonEKSClusterPolicy",
          },
        ],
        json_nodes_policy_details: [
          {
            source_prefix: "*",
          },
        ],
      },
    },
    session_id: "test",
    additional_prompt: "",
    app_name: chatType === "unit" ? "Front_Facing_Chatbot" : "Insurance_Inquiry_Application",
    query:
      chatType === "unit"
        ? `QUESTION: ${question}  RAG RESPONSE: ${data} For a generic query or a message (for e.g. hi, hello, etc), ignore the RAG RESPONSE, and say that you can answer only about Units(one line only). 
        If the query is related to a unit add a variable "query_is_related" as true else false at the start of the response.`
        : `You are an admin assistant for an insurance claims company. 
        The following report refers to the amount of anomalous alerts for specifics metrics over the past 3 months.
        Your task is to summarize the contents of the report. Include only your summary in the final response. Report: ${data}`,
  };

  try {
    const response = await chatClient.post<RetrievalResponse>("/v2.0/retrieval/chat", body, {
      headers: {
        "X-Api-Key": apiKey,
        "Content-Type": "application/json",
        // Authorization: `Bearer ${accessToken}`,
        // Authorization: axios.defaults.headers.common["Authorization"],
      },
    });

    if (!accessToken) {
      throw new Error("No access token found. Please log in again.");
    }

    if (response.status === 200 && response.data.reference_id) {
      return await getChatResponse(response.data.reference_id, apiKey, accessToken.toString(), chatType, question, data);
    }

    throw new Error("Failed to retrieve reference ID from API response.");
  } catch (error) {
    console.error("Error in postRetrievalData:", error);
    throw error; // Propagate error for external handling
  }
};

export const getChatResponse = async (referenceId: string, apiKey: string, accessToken: string, chatType: "unit" | "insurance", question: string, fullData: any): Promise<any> => {
  const response = await chatClient.get<any>(`/v1.0/history/retrieval?reference_id=${referenceId}`, {
    headers: {
      "X-Api-Key": apiKey,
      "Content-Type": "application/json",
      // Authorization: `Bearer ${accessToken}`,
      // Authorization: axios.defaults.headers.common["Authorization"],
    },
  });

  if (response.status === 200 || response.status === 202) {
    const chatData = response.data;
    // Check if the response is still in progress
    if (chatData.reference_id) {
      await delay(1000);
      return getChatResponse(referenceId, apiKey, accessToken, chatType, question, fullData);
    } else {
      // Base case: No new reference_id, return the final data
      return {
        chatData: chatData,
        fullData: fullData,
      };
    }
  } else {
    throw new Error("Failed to retrieve chat response from API.");
  }
};

export const generateReport = async (unitId: string, siteId: string, projectId: string, timeframe: number): Promise<any> => {
  return axios
    .get(`${dbUrl}units/get_unit_report?unit_id=${unitId}&site=${siteId}&project=${projectId}&interval_months=${timeframe}`)
    .then(async (response) => {
      console.log("generateReport response", response);
      return {
        reportData: response.data,
        ...(response?.data?.report !== null && {
          processed: await postRetrievalDataChat({ chatType: "insurance", data: response?.data?.report, question: "" }),
        }),
      };
    })
    .catch((error) => {
      console.error("Error in generateReport:", error);
      throw error;
    });
};
